import dayjs from "dayjs";
import { useEffect, useMemo, useRef, useState, type ChangeEvent } from "react";
import { t } from "ttag";

import { useLazyGetAdhocQueryQuery, useListDatabasesQuery } from "metabase/api";
import { useSelector } from "metabase/lib/redux";
import QuestionDownloadPopover from "metabase/query_builder/components/QuestionDownloadPopover";
import { getMetadata } from "metabase/selectors/metadata";
import {
  Box,
  Button,
  Divider,
  Group,
  Icon,
  Menu,
  MultiSelect,
  NumberInput,
  Paper,
  Select,
  Stack,
  Text,
  TextInput,
} from "metabase/ui";
import Visualization from "metabase/visualizations/components/Visualization/Visualization";
import Question from "metabase-lib/v1/Question";

import {
  createRule,
  getRowsForConfig,
  rowMap,
} from "../lib/cashflowTemplate";
import { buildCashflowDetailSql, buildCashflowSummarySql } from "../lib/sql";
import {
  createEmptyConfig,
  exportConfig,
  importConfig,
  loadConfig,
  normalizeConfig,
  saveConfig,
} from "../lib/storage";
import type {
  CashflowBuilderConfig,
  CashflowRule,
  CashflowTemplateRow,
  FormulaDefinition,
  MatchType,
} from "../types";

type SqlTab = "summary" | "detail";
type NewRowType = "section" | "leaf" | "formula";
const AUTOZEEL_CORE_DATABASE_NAME = "autozeel_core";

function getRulesForRow(config: CashflowBuilderConfig, rowId: string): CashflowRule[] {
  return config.rows[rowId] ?? [];
}

function generateInternalName(displayCode: string, label: string, fallback: string): string {
  const seed = `${displayCode}-${label}`
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");

  return seed || fallback;
}

function toSelectData(rows: CashflowTemplateRow[], config: CashflowBuilderConfig) {
  return rows.map((row) => ({
    value: row.rowId,
    label: `${config.rowMeta[row.rowId]?.displayCode ?? row.displayCode} ${config.rowMeta[row.rowId]?.label ?? row.label}`,
  }));
}

function removeDeletedRowReferences(
  formulas: Record<string, FormulaDefinition>,
  deletedRowIds: Set<string>,
): Record<string, FormulaDefinition> {
  const nextFormulas: Record<string, FormulaDefinition> = {};

  for (const [rowId, formula] of Object.entries(formulas)) {
    if (deletedRowIds.has(rowId)) {
      continue;
    }

    nextFormulas[rowId] = {
      ...formula,
      references: formula.references.filter((reference) => !deletedRowIds.has(reference)),
    };
  }

  return nextFormulas;
}

export function ReportGeneratorPage() {
  const [config, setConfig] = useState<CashflowBuilderConfig>(() => loadConfig());
  const initialRows = getRowsForConfig(config);
  const initialSelectedRowId =
    initialRows.find((row) => row.rowId === "operating_loans_interest_received")?.rowId ??
    initialRows[0]?.rowId ??
    "";
  const [selectedRowId, setSelectedRowId] = useState<string>(initialSelectedRowId);
  const [exportMode, setExportMode] = useState<SqlTab>("summary");
  const [statusMessage, setStatusMessage] = useState<string>(t`Ready`);
  const importInputRef = useRef<HTMLInputElement>(null);
  const metadata = useSelector(getMetadata);

  const summarySql = useMemo(() => buildCashflowSummarySql(config), [config]);
  const detailSql = useMemo(() => buildCashflowDetailSql(config), [config]);
  const sqlOutput = exportMode === "summary" ? summarySql : detailSql;
  const { data: databasesResponse } = useListDatabasesQuery();
  const autozeelCoreDatabase = useMemo(
    () =>
      databasesResponse?.data?.find(
        (database) => database.name === AUTOZEEL_CORE_DATABASE_NAME,
      ) ?? null,
    [databasesResponse],
  );
  const [runAdhocQuery, { data: queryResult, isFetching: isRunningQuery }] =
    useLazyGetAdhocQueryQuery();
  const adHocQuestion = useMemo(() => {
    if (!metadata || !autozeelCoreDatabase?.id) {
      return null;
    }

    return Question.create({
      dataset_query: {
        type: "native",
        database: autozeelCoreDatabase.id,
        native: {
          query: sqlOutput,
        },
      },
      metadata,
      cardType: "question",
      display: "table",
      visualization_settings: {},
    });
  }, [metadata, autozeelCoreDatabase?.id, sqlOutput]);
  const rawSeries =
    adHocQuestion && queryResult
      ? [{ card: adHocQuestion.card(), data: queryResult.data }]
      : null;

  useEffect(() => {
    saveConfig(config);
  }, [config]);

  const allRows = useMemo(() => getRowsForConfig(config), [config]);
  const selectedRow =
    allRows.find((row) => row.rowId === selectedRowId) ?? allRows[0] ?? null;
  const selectedRules = selectedRow ? getRulesForRow(config, selectedRow.rowId) : [];

  const editableCount = allRows
    .filter((row) => row.rowType === "leaf")
    .reduce(
    (total, row) => total + getRulesForRow(config, row.rowId).length,
    0,
  );

  useEffect(() => {
    if (!selectedRow && allRows.length > 0) {
      setSelectedRowId(allRows[0].rowId);
    }
  }, [allRows, selectedRow]);

  function updateConfig(next: CashflowBuilderConfig) {
    setConfig(normalizeConfig(next));
  }

  function updateGlobal<K extends keyof CashflowBuilderConfig>(
    key: K,
    value: CashflowBuilderConfig[K],
  ) {
    updateConfig({
      ...config,
      [key]: value,
    });
  }

  function updateRule(ruleId: string, patch: Partial<CashflowRule>) {
    if (!selectedRow) {
      return;
    }

    const nextRows = {
      ...config.rows,
      [selectedRow.rowId]: selectedRules.map((rule) =>
        rule.id === ruleId ? { ...rule, ...patch } : rule,
      ),
    };

    updateConfig({
      ...config,
      rows: nextRows,
    });
  }

  function updateRowMeta(
    field: "displayCode" | "label" | "internalName",
    value: string,
  ) {
    if (!selectedRow) {
      return;
    }

    const current = config.rowMeta[selectedRowId] ?? {
      displayCode: selectedRow.displayCode,
      label: selectedRow.label,
      internalName: selectedRow.internalName ?? selectedRowId,
    };

    const nextMeta = {
      ...current,
      [field]: value,
    };

    nextMeta.internalName = generateInternalName(
      nextMeta.displayCode,
      nextMeta.label,
      selectedRowId,
    );

    updateConfig({
      ...config,
      rowMeta: {
        ...config.rowMeta,
        [selectedRowId]: nextMeta,
      },
    });
  }

  function updateFormula(patch: Partial<FormulaDefinition>) {
    if (!selectedRow) {
      return;
    }

    const current = config.formulas[selectedRowId] ?? selectedRow.formula;
    if (!current) {
      return;
    }

    const nextFormula: FormulaDefinition = {
      ...current,
      ...patch,
    };

    if (nextFormula.kind === "diff") {
      nextFormula.references = [...nextFormula.references, "", ""].slice(0, 2);
    }

    updateConfig({
      ...config,
      formulas: {
        ...config.formulas,
        [selectedRowId]: nextFormula,
      },
    });
  }

  function addCustomRow(rowType: NewRowType = "leaf") {
    if (!selectedRow && rowType !== "section") {
      return;
    }

    if (rowType === "section") {
      const customSectionCount = config.customRows.filter(
        (row) => row.rowType === "section",
      ).length;
      const nextIndex = customSectionCount + 1;
      const rowId = `custom_section_${nextIndex}`;
      const maxOrder = allRows.reduce((max, row) => Math.max(max, row.order), 0);
      const numericSectionCodes = allRows
        .filter((row) => row.rowType === "section")
        .map((row) => Number.parseInt(row.displayCode.split(".")[0], 10))
        .filter((value) => Number.isFinite(value));
      const nextSectionCode =
        (numericSectionCodes.length > 0 ? Math.max(...numericSectionCodes) : 0) +
        1;
      const displayCode = `${nextSectionCode}.0`;
      const label = `${t`Section`} ${nextSectionCode}`;
      const internalName = generateInternalName(displayCode, label, rowId);

      updateConfig({
        ...config,
        customRows: [
          ...config.customRows,
          {
            rowId,
            internalName,
            displayCode,
            label,
            rowType,
            order: maxOrder + 1,
            sectionId: rowId,
          },
        ],
        rowMeta: {
          ...config.rowMeta,
          [rowId]: {
            displayCode,
            label,
            internalName,
          },
        },
      });
      setSelectedRowId(rowId);
      setStatusMessage(t`Added section row`);
      return;
    }

    const sectionId =
      selectedRow?.rowType === "section" ? selectedRow.rowId : selectedRow?.sectionId;
    if (!sectionId) {
      return;
    }
    const sectionRow = allRows.find((row) => row.rowId === sectionId);
    const sectionPrefix = sectionRow?.displayCode.split(".")[0] ?? "X";

    const customRowsOfType = config.customRows.filter(
      (row) => row.sectionId === sectionId && row.rowType === rowType,
    );
    const nextIndex = customRowsOfType.length + 1;

    const rowId = `custom_${rowType === "formula" ? "formula" : "leaf"}_${sectionId}_${nextIndex}`;
    const displayCode = `${sectionPrefix}.${rowType === "formula" ? "f" : "x"}${nextIndex}`;
    const rowTypeLabel = rowType === "formula" ? t`Formula` : t`Custom`;
    const label = `${rowTypeLabel} ${t`row`} ${nextIndex}`;

    const sectionLeafMaxOrder = allRows
      .filter((row) => row.sectionId === sectionId && row.rowType === "leaf")
      .reduce((max, row) => Math.max(max, row.order), 0);
    const sectionFormulaMinOrder = allRows
      .filter((row) => row.sectionId === sectionId && row.rowType === "formula")
      .reduce((min, row) => Math.min(min, row.order), Number.POSITIVE_INFINITY);

    const nextOrder = Number.isFinite(sectionFormulaMinOrder)
      ? Math.min(sectionLeafMaxOrder + 0.1, sectionFormulaMinOrder - 0.1)
      : sectionLeafMaxOrder + 0.1;

    const internalName = generateInternalName(displayCode, label, rowId);

    updateConfig({
      ...config,
      customRows: [
        ...config.customRows,
        {
          rowId,
          internalName,
          displayCode,
          label,
          rowType,
          order: nextOrder,
          sectionId,
        },
      ],
      rowMeta: {
        ...config.rowMeta,
        [rowId]: {
          displayCode,
          label,
          internalName,
        },
      },
      rows: {
        ...config.rows,
        ...(rowType === "leaf" ? { [rowId]: [] } : {}),
      },
      formulas:
        rowType === "formula"
          ? {
              ...config.formulas,
              [rowId]: {
                kind: "sum",
                references: [],
                stage: 1,
                description: label,
              },
            }
          : config.formulas,
    });

    setSelectedRowId(rowId);
    setStatusMessage(
      rowType === "formula" ? t`Added formula row` : t`Added editable row`,
    );
  }

  function deleteSelectedRow() {
    if (!selectedRow) {
      return;
    }

    const selectedIsTemplate = rowMap.has(selectedRowId);
    const rowsToDelete = new Set<string>([selectedRow.rowId]);

    if (selectedRow.rowType === "section") {
      allRows
        .filter((row) => row.sectionId === selectedRow.rowId && row.rowId !== selectedRow.rowId)
        .forEach((row) => rowsToDelete.add(row.rowId));
    }

    const rowLabel = config.rowMeta[selectedRowId]?.label ?? selectedRow.label;
    const updatedCustomRows = config.customRows.filter((row) => !rowsToDelete.has(row.rowId));
    const updatedRemovedTemplateRowIds = Array.from(
      new Set([
        ...config.removedTemplateRowIds,
        ...Array.from(rowsToDelete).filter((rowId) => rowMap.has(rowId)),
      ]),
    );

    const updatedRowMeta = Object.fromEntries(
      Object.entries(config.rowMeta).filter(([rowId]) => !rowsToDelete.has(rowId)),
    );

    const updatedRows = Object.fromEntries(
      Object.entries(config.rows).filter(([rowId]) => !rowsToDelete.has(rowId)),
    );

    const updatedFormulas = removeDeletedRowReferences(config.formulas, rowsToDelete);

    const remainingRows = allRows.filter((row) => !rowsToDelete.has(row.rowId));
    const nextSelectedRowId = remainingRows[0]?.rowId ?? "";

    updateConfig({
      ...config,
      removedTemplateRowIds: updatedRemovedTemplateRowIds,
      customRows: updatedCustomRows,
      rowMeta: updatedRowMeta,
      rows: updatedRows,
      formulas: updatedFormulas,
    });

    setSelectedRowId(nextSelectedRowId);
    if (selectedRow.rowType === "section") {
      setStatusMessage(t`Deleted section ${rowLabel} and its rows`);
      return;
    }

    setStatusMessage(
      selectedIsTemplate ? t`Removed template row ${rowLabel}` : t`Deleted ${rowLabel}`,
    );
  }

  function addRule() {
    if (!selectedRow) {
      return;
    }

    if (selectedRow.rowType !== "leaf") {
      return;
    }

    updateConfig({
      ...config,
      rows: {
        ...config.rows,
        [selectedRowId]: [...selectedRules, createRule(selectedRowId)],
      },
    });

    setStatusMessage(t`Added rule`);
  }

  function removeRule(ruleId: string) {
    if (!selectedRow) {
      return;
    }

    updateConfig({
      ...config,
      rows: {
        ...config.rows,
        [selectedRowId]: selectedRules.filter((rule) => rule.id !== ruleId),
      },
    });

    setStatusMessage(t`Removed rule`);
  }

  function startImportConfig() {
    importInputRef.current?.click();
  }

  async function onImportConfigFile(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    event.target.value = "";

    if (!file) {
      return;
    }

    try {
      const importedConfig = await importConfig(file);
      const importedRows = getRowsForConfig(importedConfig);
      updateConfig(importedConfig);
      setSelectedRowId(importedRows[0]?.rowId ?? "");
      setStatusMessage(t`Config imported`);
    } catch {
      setStatusMessage(t`Invalid config file`);
    }
  }

  function createNewEmptyTemplate() {
    const nextConfig = createEmptyConfig();
    updateConfig(nextConfig);
    setSelectedRowId("");
    setStatusMessage(t`Created empty template`);
  }

  function exportSql() {
    const sql = exportMode === "summary" ? summarySql : detailSql;
    const blob = new Blob([sql], { type: "text/sql;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = `cashflow-${exportMode}.sql`;
    anchor.click();
    URL.revokeObjectURL(url);

    setStatusMessage(
      exportMode === "summary" ? t`Summary SQL exported` : t`Detail SQL exported`,
    );
  }

  async function runCurrentQuery() {
    if (!autozeelCoreDatabase?.id) {
      setStatusMessage(t`Database autozeel_core is not available`);
      return;
    }

    try {
      const result = await runAdhocQuery({
        type: "native",
        database: autozeelCoreDatabase.id,
        native: {
          query: sqlOutput,
        },
      }).unwrap();

      setStatusMessage(
        t`Query executed on autozeel_core (${result.row_count} rows)`,
      );
    } catch {
      setStatusMessage(t`Query execution failed`);
    }
  }

  function setDateValue(key: "startDate" | "endDate", value: string | null) {
    if (!value) {
      return;
    }

    const parsed = dayjs(value);
    if (!parsed.isValid()) {
      return;
    }

    updateGlobal(key, parsed.format("YYYY-MM-DD"));
  }

  const activeFormula = selectedRow
    ? (config.formulas[selectedRowId] ?? selectedRow.formula)
    : undefined;
  const formulaReferenceOptions = allRows.filter((row) => {
    if (row.rowType === "section" || row.rowId === selectedRowId || !activeFormula) {
      return false;
    }

    const rowFormula = config.formulas[row.rowId];

    if (activeFormula.stage === 1) {
      return row.rowType === "leaf";
    }

    if (activeFormula.stage === 2) {
      return row.rowType === "leaf" || rowFormula?.stage === 1;
    }

    return (
      row.rowType === "leaf" || rowFormula?.stage === 1 || rowFormula?.stage === 2
    );
  });

  const formulaReferenceData = toSelectData(formulaReferenceOptions, config);

  return (
    <Box p="xl" maw="100%" style={{ overflow: "auto" }}>
      <Stack gap="md">
          <Group justify="space-between" align="flex-start">
            <Stack gap={4}>
              <Text fw={700} fz="1.375rem">{t`Report Generator`}</Text>
              <Text c="text-medium">{t`Build cashflow mappings and generate SQL output.`}</Text>
            </Stack>
            <Group>
              <input
                ref={importInputRef}
                type="file"
                accept="application/json,.json"
                style={{ display: "none" }}
                onChange={(event) => void onImportConfigFile(event)}
              />
              <Button
                variant="default"
                leftSection={<Icon name="document" />}
                onClick={startImportConfig}
              >
                {t`Import config`}
              </Button>
              <Button
                variant="default"
                leftSection={<Icon name="add" />}
                onClick={createNewEmptyTemplate}
              >
                {t`New empty`}
              </Button>
              <Button
                variant="default"
                leftSection={<Icon name="download" />}
                onClick={() => exportConfig(config)}
              >
                {t`Export config`}
              </Button>
            </Group>
          </Group>

        <Paper p="md" withBorder>
          <Group align="end" justify="space-between" wrap="wrap">
            <Group align="end" wrap="wrap">
              <TextInput
                label={t`Start`}
                placeholder="YYYY-MM-DD"
                value={config.startDate}
                onChange={(event) => setDateValue("startDate", event.target.value)}
                w={180}
              />
              <TextInput
                label={t`End`}
                placeholder="YYYY-MM-DD"
                value={config.endDate}
                onChange={(event) => setDateValue("endDate", event.target.value)}
                w={180}
              />
              <Select
                label={t`SQL type`}
                value={exportMode}
                onChange={(value) => setExportMode((value as SqlTab) ?? "summary")}
                data={[
                  { value: "summary", label: t`Summary` },
                  { value: "detail", label: t`Detail` },
                ]}
                w={160}
              />
              <Button
                leftSection={<Icon name="play" />}
                onClick={() => void runCurrentQuery()}
                loading={isRunningQuery}
                disabled={!autozeelCoreDatabase}
              >
                {t`Run query`}
              </Button>
            </Group>

            <Text c="text-medium">{statusMessage}</Text>
          </Group>
        </Paper>

        <Group grow align="flex-start" wrap="nowrap">
          <Paper p="md" withBorder style={{ minWidth: 620, maxWidth: 780, width: "55%" }}>
            <Group justify="space-between" mb="sm">
              <Text fw={700}>{t`Cashflow rows`}</Text>
              <Group gap="xs">
                <Menu>
                  <Menu.Target>
                    <Button variant="subtle" leftSection={<Icon name="add" />} rightSection={<Icon name="chevrondown" />}>
                      {t`Add row`}
                    </Button>
                  </Menu.Target>
                  <Menu.Dropdown>
                    <Menu.Item onClick={() => addCustomRow("section")}>{t`Section row`}</Menu.Item>
                    <Menu.Item onClick={() => addCustomRow("leaf")}>{t`Editable row`}</Menu.Item>
                    <Menu.Item onClick={() => addCustomRow("formula")}>{t`Formula row`}</Menu.Item>
                  </Menu.Dropdown>
                </Menu>

                <Button
                  variant="subtle"
                  color="error"
                  leftSection={<Icon name="trash" />}
                  disabled={!selectedRow}
                  onClick={deleteSelectedRow}
                >
                  {t`Remove`}
                </Button>
              </Group>
            </Group>

            {allRows.length === 0 && (
              <Text c="text-medium" mb="sm">
                {t`No rows available. Add a section first.`}
              </Text>
            )}

            <Box style={{ maxHeight: 560, overflow: "auto" }}>
              <table style={{ width: "100%" }}>
                <thead>
                  <tr>
                    <th align="left">{t`Code`}</th>
                    <th align="left">{t`Row`}</th>
                    <th align="left">{t`Type`}</th>
                    <th align="right">{t`Rules`}</th>
                  </tr>
                </thead>
                <tbody>
                  {allRows.map((row) => {
                    const selected = row.rowId === selectedRowId;
                    const rules = getRulesForRow(config, row.rowId);

                    return (
                      <tr
                        key={row.rowId}
                        style={{ cursor: "pointer", background: selected ? "var(--mb-color-bg-light)" : undefined }}
                        onClick={() => setSelectedRowId(row.rowId)}
                      >
                        <td>{config.rowMeta[row.rowId]?.displayCode ?? row.displayCode}</td>
                        <td>{config.rowMeta[row.rowId]?.label ?? row.label}</td>
                        <td>{row.rowType}</td>
                        <td align="right">{row.rowType === "leaf" ? rules.length : "-"}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </Box>
          </Paper>

          <Paper p="md" withBorder style={{ minWidth: 520, width: "45%" }}>
            <Stack gap="md">
              {!selectedRow && (
                <Paper p="sm" withBorder>
                  <Text c="text-medium">
                    {t`No rows in this template. Add a row to continue editing.`}
                  </Text>
                </Paper>
              )}

              {selectedRow && (
                <>
              <Group justify="space-between" align="flex-start">
                <Stack gap={4}>
                  <Text fw={700}>
                    {(config.rowMeta[selectedRowId]?.displayCode ?? selectedRow.displayCode) +
                      " " +
                      (config.rowMeta[selectedRowId]?.label ?? selectedRow.label)}
                  </Text>
                  {selectedRow.rowType === "leaf" && (
                    <Text c="text-medium">{t`${selectedRules.length} rules`}</Text>
                  )}
                </Stack>

                {selectedRow.rowType === "leaf" && (
                  <Button leftSection={<Icon name="add" />} onClick={addRule}>
                    {t`Add rule`}
                  </Button>
                )}
              </Group>

              <Group grow>
                <TextInput
                  label={t`Code`}
                  value={config.rowMeta[selectedRowId]?.displayCode ?? selectedRow.displayCode}
                  onChange={(event) => updateRowMeta("displayCode", event.target.value)}
                />
                <TextInput
                  label={t`Row name`}
                  value={config.rowMeta[selectedRowId]?.label ?? selectedRow.label}
                  onChange={(event) => updateRowMeta("label", event.target.value)}
                />
              </Group>

              {selectedRow.rowType === "leaf" && (
                <table style={{ width: "100%" }}>
                  <thead>
                    <tr>
                      <th align="left">{t`Debit type`}</th>
                      <th align="left">{t`Credit type`}</th>
                      <th align="left">{t`Side`}</th>
                      <th align="left">{t`Debit value`}</th>
                      <th align="left">{t`Credit value`}</th>
                      <th align="left">{t`Coeff`}</th>
                      <th align="right">{t`Actions`}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedRules.length === 0 && (
                      <tr>
                        <td colSpan={7}>
                          <Text c="text-medium">{t`No rules yet`}</Text>
                        </td>
                      </tr>
                    )}

                    {selectedRules.map((rule) => (
                      <tr key={rule.id}>
                        <td>
                          <Select
                            aria-label={t`Debit match type`}
                            value={rule.dt_match_type}
                            onChange={(value) =>
                              updateRule(rule.id, {
                                dt_match_type: (value as MatchType) ?? "gl_code",
                              })
                            }
                            data={[
                              { value: "gl_code", label: t`GL code` },
                              { value: "acct_num", label: t`Account` },
                            ]}
                          />
                        </td>
                        <td>
                          <Select
                            aria-label={t`Credit match type`}
                            value={rule.kt_match_type}
                            onChange={(value) =>
                              updateRule(rule.id, {
                                kt_match_type: (value as MatchType) ?? "gl_code",
                              })
                            }
                            data={[
                              { value: "gl_code", label: t`GL code` },
                              { value: "acct_num", label: t`Account` },
                            ]}
                          />
                        </td>
                        <td>
                          <Select
                            aria-label={t`Rule side`}
                            value={rule.amountSide}
                            onChange={(value) =>
                              updateRule(rule.id, {
                                amountSide: (value as CashflowRule["amountSide"]) ?? "CR",
                              })
                            }
                            data={[
                              { value: "CR", label: t`Credit` },
                              { value: "DR", label: t`Debit` },
                            ]}
                          />
                        </td>
                        <td>
                          <TextInput
                            value={rule.debitValue}
                            onChange={(event) =>
                              updateRule(rule.id, { debitValue: event.target.value })
                            }
                            placeholder={
                              rule.dt_match_type === "gl_code" ? "11211000" : "ACC-DT-001"
                            }
                          />
                        </td>
                        <td>
                          <TextInput
                            value={rule.creditValue}
                            onChange={(event) =>
                              updateRule(rule.id, { creditValue: event.target.value })
                            }
                            placeholder={
                              rule.kt_match_type === "gl_code" ? "42310000" : "ACC-KT-999"
                            }
                          />
                        </td>
                        <td>
                          <NumberInput
                            value={rule.coefficient}
                            step={0.01}
                            onChange={(value) =>
                              updateRule(rule.id, {
                                coefficient:
                                  typeof value === "number"
                                    ? value
                                    : Number.parseFloat(String(value || "1")),
                              })
                            }
                          />
                        </td>
                        <td>
                          <Group justify="flex-end" gap="xs">
                            <Button
                              variant="subtle"
                              color="error"
                              leftSection={<Icon name="trash" />}
                              onClick={() => removeRule(rule.id)}
                            >
                              {t`Delete`}
                            </Button>
                          </Group>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}

              {selectedRow.rowType === "formula" && activeFormula && (
                <Stack gap="sm">
                  <Select
                    label={t`Operation`}
                    value={activeFormula.kind}
                    onChange={(value) =>
                      updateFormula({
                        kind: (value as FormulaDefinition["kind"]) ?? "sum",
                        references:
                          value === "diff"
                            ? [...activeFormula.references, "", ""].slice(0, 2)
                            : activeFormula.references,
                      })
                    }
                    data={[
                      { value: "sum", label: t`Sum` },
                      { value: "diff", label: t`Difference` },
                    ]}
                    maw={240}
                  />

                  {activeFormula.kind === "sum" ? (
                    <MultiSelect
                      label={t`Source rows`}
                      data={formulaReferenceData}
                      value={activeFormula.references}
                      searchable
                      onChange={(value) => updateFormula({ references: value })}
                      styles={{
                        input: {
                          color: "var(--mb-color-text-primary)",
                          backgroundColor: "var(--mb-color-bg-white)",
                          borderColor: "var(--mb-color-border)",
                        },
                        pill: {
                          color: "var(--mb-color-text-primary)",
                          backgroundColor: "var(--mb-color-bg-light)",
                        },
                        dropdown: {
                          backgroundColor: "var(--mb-color-bg-white)",
                        },
                        option: {
                          color: "var(--mb-color-text-primary)",
                        },
                      }}
                    />
                  ) : (
                    <Group grow>
                      {[0, 1].map((index) => (
                        <Select
                          key={index}
                          label={index === 0 ? t`Left row` : t`Right row`}
                          data={formulaReferenceData}
                          value={activeFormula.references[index] ?? ""}
                          onChange={(value) => {
                            const nextRefs = [...activeFormula.references, "", ""].slice(0, 2);
                            nextRefs[index] = value ?? "";
                            updateFormula({ references: nextRefs });
                          }}
                        />
                      ))}
                    </Group>
                  )}
                </Stack>
              )}

              {selectedRow.rowType === "section" && (
                <Paper p="sm" withBorder>
                  <Text fw={700}>{t`Section row`}</Text>
                  <Text c="text-medium">
                    {t`This row is not directly editable. Select an editable row or add a custom row under this section.`}
                  </Text>
                </Paper>
              )}
                </>
              )}
            </Stack>
          </Paper>
        </Group>

        <Divider />

        <Paper p="md" withBorder>
          <Group justify="space-between" mb="sm">
            <Text fw={700}>{t`SQL`}</Text>
            <Button variant="default" leftSection={<Icon name="download" />} onClick={exportSql}>
              {t`Download SQL`}
            </Button>
          </Group>
          <Text c="text-medium">
            {t`Generated SQL is hidden. Use Download SQL to get the query file.`}
          </Text>

          <Group mt="md" justify="space-between">
            <Text c="text-medium">
              {t`${editableCount} total editable-rule mappings configured`}
            </Text>
          </Group>
        </Paper>

        <Paper p="md" withBorder>
          <Stack gap="sm">
            <Group justify="space-between">
              <Text fw={700}>{t`Query results`}</Text>
              <Text c="text-medium">
                {autozeelCoreDatabase
                  ? t`Database: autozeel_core`
                  : t`Database autozeel_core is not available`}
              </Text>
            </Group>

            {!queryResult && (
              <Text c="text-medium">
                {t`Run the query to see rows here, just like a question result table.`}
              </Text>
            )}

            {adHocQuestion && queryResult && rawSeries && (
              <Box>
                <Group justify="flex-end" mb="xs">
                  {QuestionDownloadPopover.shouldRender({ result: queryResult }) && (
                    <QuestionDownloadPopover
                      question={adHocQuestion}
                      result={queryResult}
                      variant="subtle"
                      visualizationSettings={{}}
                    />
                  )}
                </Group>
                <Box h={420}>
                  <Visualization
                    rawSeries={rawSeries}
                    metadata={adHocQuestion.metadata()}
                    isRunning={isRunningQuery}
                    isQueryBuilder
                    isEditing
                    isShowingDetailsOnlyColumns={false}
                    showTitle={false}
                  />
                </Box>
              </Box>
            )}
          </Stack>
        </Paper>
      </Stack>
    </Box>
  );
}
